import { omit } from 'lodash'
import * as Yup from 'yup'
import { getBrowserTimeZone } from '../../../../../util/date.util'
import { getValidationSchema as getScheduleValidationSchema } from '../common/Scheduling/fieldset'
import { FORM_FIELDS as DEFAULT_SETTINGS_FIELDS } from '../OffboardDefaultSettings/constants'
import { FORM_FIELDS, INITIAL_VALUES } from './constants'

// all groups that are not in the groupsToKeep array will be selected for removal
const populateGroupsToBeRemoved = (allGroupOptions, groupsToKeep) => {
  const keep = {}
  groupsToKeep?.forEach((id) => {
    keep[id] = true
  })

  return allGroupOptions.filter((option) => !keep[option.value]).map((option) => option.value)
}

/**
 * build initial values based on data that is provided externally
 */
export const buildInitialValues = ({
  userInfo = {},
  isUserMailBoxAvailable,
  defaultSettings,
  m365GroupOptions,
  securityGroupOptions,
  distributionGroupOptions,
  sharedMailboxOptions,
}) => {
  const { delegatedPermissions, emailAlias } = userInfo

  const initialValues = {
    ...INITIAL_VALUES,

    // for offboarding the fields saved in default settings are named exactly the same
    // if there is any mismatch, adapt fields here
    ...omit(defaultSettings, [
      DEFAULT_SETTINGS_FIELDS.KEEP_IN_DISTRIBUTION_GROUPS, // omiting default settings group fields from initial values as they are going to be handled below
      DEFAULT_SETTINGS_FIELDS.KEEP_IN_M365_GROUPS,
      DEFAULT_SETTINGS_FIELDS.KEEP_IN_SECURITY_GROUPS,
      DEFAULT_SETTINGS_FIELDS.KEEP_IN_SHARED_MAILBOXES,
    ]),
  }

  // loop through the form fields and applying the default INITIAL_VALUES if any default setting came as undefined
  Object.values(FORM_FIELDS).forEach((field) => {
    if (initialValues[field] === undefined) {
      initialValues[field] = INITIAL_VALUES[field]
    }
  })

  // remove options that cannot be selected based on the data loaded
  if (!isUserMailBoxAvailable) {
    // turn off forward mailbox fields
    initialValues[FORM_FIELDS.IS_FOWARDING_MAILBOX] = false
    initialValues[FORM_FIELDS.FORWARD_MAILBOX_TO] = INITIAL_VALUES[FORM_FIELDS.FORWARD_MAILBOX_TO]
    initialValues[FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED] =
      INITIAL_VALUES[FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED]

    // turn off shared mailbox fields
    initialValues[FORM_FIELDS.CONVERT_TO_SHARED_MAILBOX] = false
    initialValues[FORM_FIELDS.MAILBOX_USERS] = INITIAL_VALUES[FORM_FIELDS.MAILBOX_USERS]

    // turn off send automatic replies
    initialValues[FORM_FIELDS.SEND_AUTOMATIC_REPLIES] = false
    initialValues[FORM_FIELDS.SEND_REPLIES_DURING_PERIOD] = false
    initialValues[FORM_FIELDS.AUTO_REPLY_START_DATE] = INITIAL_VALUES[FORM_FIELDS.AUTO_REPLY_START_DATE]
    initialValues[FORM_FIELDS.AUTO_REPLY_END_DATE] = INITIAL_VALUES[FORM_FIELDS.AUTO_REPLY_END_DATE]
    initialValues[FORM_FIELDS.INTERNAL_REPLY_MESSAGE] = INITIAL_VALUES[FORM_FIELDS.INTERNAL_REPLY_MESSAGE]
    initialValues[FORM_FIELDS.EXTERNAL_REPLY_MESSAGE] = INITIAL_VALUES[FORM_FIELDS.EXTERNAL_REPLY_MESSAGE]

    // turn off hide from global address list
    initialValues[FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED] = false
  }

  if (!emailAlias?.length) {
    initialValues[FORM_FIELDS.REMOVE_EMAIL_ALIASES] = false
  }

  if (!delegatedPermissions?.length) {
    initialValues[FORM_FIELDS.REMOVE_DELEGATE_PERMISSIONS] = false
  }

  // mark all groups to be removed (unless they are set to be kept in the default settings)
  if (m365GroupOptions?.length) {
    initialValues[FORM_FIELDS.REMOVE_FROM_M365_GROUPS] = populateGroupsToBeRemoved(
      m365GroupOptions,
      defaultSettings[DEFAULT_SETTINGS_FIELDS.KEEP_IN_M365_GROUPS],
    )
  }

  if (securityGroupOptions?.length) {
    initialValues[FORM_FIELDS.REMOVE_FROM_SECURITY_GROUPS] = populateGroupsToBeRemoved(
      securityGroupOptions,
      defaultSettings[DEFAULT_SETTINGS_FIELDS.KEEP_IN_SECURITY_GROUPS],
    )
  }

  if (distributionGroupOptions?.length) {
    initialValues[FORM_FIELDS.REMOVE_FROM_DISTRIBUTION_GROUPS] = populateGroupsToBeRemoved(
      distributionGroupOptions,
      defaultSettings[DEFAULT_SETTINGS_FIELDS.KEEP_IN_DISTRIBUTION_GROUPS],
    )
  }

  if (sharedMailboxOptions?.length) {
    initialValues[FORM_FIELDS.REMOVE_FROM_SHARED_MAILBOXES] = populateGroupsToBeRemoved(
      sharedMailboxOptions,
      defaultSettings[DEFAULT_SETTINGS_FIELDS.KEEP_IN_SHARED_MAILBOXES],
    )
  }

  return initialValues
}

export const getValidationSchema = (intl) => {
  const requiredMessage = intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
  const startDateValidationMessage = intl.formatMessage({ id: 'VALIDATION.START_TIME_GT_CURRENT_TIME' })
  const endDateValidationMessage = intl.formatMessage({ id: 'VALIDATION.END_TIME_GT_START_TIME' })

  const scheduleValidationSchema = getScheduleValidationSchema(intl)

  return Yup.object()
    .shape({
      // email section
      [FORM_FIELDS.REMOVE_EMAIL_ALIASES]: Yup.boolean(),
      [FORM_FIELDS.REMOVE_DELEGATE_PERMISSIONS]: Yup.boolean(),
      [FORM_FIELDS.IS_FOWARDING_MAILBOX]: Yup.boolean(),
      [FORM_FIELDS.FORWARD_MAILBOX_TO]: Yup.string()
        .nullable()
        .when(FORM_FIELDS.IS_FOWARDING_MAILBOX, {
          is: true,
          then: () => Yup.string().required(requiredMessage),
        }),
      [FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED]: Yup.boolean(),

      // shared mailbox section
      [FORM_FIELDS.CONVERT_TO_SHARED_MAILBOX]: Yup.boolean(),

      // auto reply section
      [FORM_FIELDS.SEND_AUTOMATIC_REPLIES]: Yup.boolean(),
      [FORM_FIELDS.SEND_REPLIES_DURING_PERIOD]: Yup.boolean(),

      // auto reply section - date and time validation
      [FORM_FIELDS.AUTO_REPLY_START_DATE]: Yup.date().when(FORM_FIELDS.SEND_REPLIES_DURING_PERIOD, {
        is: true,
        then: () => Yup.date().required(requiredMessage).min(new Date(), startDateValidationMessage),
      }),
      [FORM_FIELDS.AUTO_REPLY_END_DATE]: Yup.date().when(FORM_FIELDS.SEND_REPLIES_DURING_PERIOD, {
        is: true,
        then: () =>
          Yup.date()
            .required(requiredMessage)
            .min(Yup.ref(FORM_FIELDS.AUTO_REPLY_START_DATE), endDateValidationMessage),
      }),

      // auto reply section - reply message validation
      [FORM_FIELDS.INTERNAL_REPLY_MESSAGE]: Yup.string().when(FORM_FIELDS.SEND_AUTOMATIC_REPLIES, {
        is: true,
        then: () => Yup.string().required(requiredMessage),
      }),
      [FORM_FIELDS.EXTERNAL_REPLY_MESSAGE]: Yup.string(),
    })
    .concat(scheduleValidationSchema) // schedule section
}

export const toApiPayload = ({ values, employee, userInfo }) => ({
  employeeIdentifier: employee?.employeeIdentifier,
  emailAliasesToBeRemoved: values[FORM_FIELDS.REMOVE_EMAIL_ALIASES] ? userInfo?.emailAlias : [],
  removeDelegatePermissions: values[FORM_FIELDS.REMOVE_DELEGATE_PERMISSIONS],
  forwardMailboxTo: values[FORM_FIELDS.FORWARD_MAILBOX_TO],
  hiddenFromAddressListEnabled: values[FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED],
  sharedMailboxConfig: values[FORM_FIELDS.CONVERT_TO_SHARED_MAILBOX]
    ? {
        mailboxUsers: values[FORM_FIELDS.MAILBOX_USERS],
      }
    : null,
  automaticRepliesSetting: values[FORM_FIELDS.SEND_AUTOMATIC_REPLIES]
    ? {
        status: values[FORM_FIELDS.SEND_REPLIES_DURING_PERIOD] ? 'Scheduled' : 'Enabled',
        scheduledStartDateTime: {
          dateTime: values[FORM_FIELDS.AUTO_REPLY_START_DATE]
            ? new Date(values[FORM_FIELDS.AUTO_REPLY_START_DATE]).toISOString()
            : new Date().toISOString(),
          timeZone: getBrowserTimeZone(),
        },
        scheduledEndDateTime: {
          dateTime: values[FORM_FIELDS.AUTO_REPLY_END_DATE]
            ? new Date(values[FORM_FIELDS.AUTO_REPLY_END_DATE]).toISOString()
            : new Date().toISOString(),
          timeZone: getBrowserTimeZone(),
        },
        internalReplyMessage: values[FORM_FIELDS.INTERNAL_REPLY_MESSAGE] || null,
        externalReplyMessage: values[FORM_FIELDS.EXTERNAL_REPLY_MESSAGE] || null,
      }
    : null,
  revokeSessions: values[FORM_FIELDS.REVOKE_SESSIONS],
  disableUser: values[FORM_FIELDS.DISABLE_USER],
  resetPassword: values[FORM_FIELDS.RESET_PASSWORD],
  groupsToBeRemoved: [
    ...values[FORM_FIELDS.REMOVE_FROM_M365_GROUPS],
    ...values[FORM_FIELDS.REMOVE_FROM_SECURITY_GROUPS],
  ],
  distributionGroupsToBeRemoved: values[FORM_FIELDS.REMOVE_FROM_DISTRIBUTION_GROUPS],
  sharedMailboxesToBeRemoved: values[FORM_FIELDS.REMOVE_FROM_SHARED_MAILBOXES],
  deleteUser: false,
})
